import { Id } from "./Id.tsx";
import { LocalTime } from "./LocalTime.tsx";
import type { LiveMode, RunState } from "./live.ts";
import { POLL_MS, STALE_MS } from "./live.ts";
import { Badge, facts } from "./ui.tsx";

/** How long ago, in words: "just now", "4 min ago", "2 h 5 min ago". */
export function agoText(time: string, now: number): string {
  const minutes = Math.floor((now - Date.parse(time)) / 60_000);
  if (!Number.isFinite(minutes)) return time;
  if (minutes < 1) return "just now";
  if (minutes < 60) return `${minutes} min ago`;
  return `${Math.floor(minutes / 60)} h ${minutes % 60} min ago`;
}

/** An active run whose machine has said nothing for longer than the idle: probably gone. */
export function isStale(state: RunState, now: number): boolean {
  return state.state === "active" && now - Date.parse(state.lastActivityAt) > STALE_MS;
}

function holderText(state: RunState): string | undefined {
  const holder = state.holder;
  return holder ? (holder.email ?? holder.producerId) : undefined;
}

/** A run's live state in one cell: state, holder, page, last activity. */
export function LiveSummary({ state, now }: { state: RunState | undefined; now: number }) {
  if (!state) return <span className="text-muted">—</span>;
  const stale = isStale(state, now);
  return (
    <span className="leading-relaxed">
      <StateBadge state={state} stale={stale} />
      {holderText(state) && <> · {holderText(state)}</>}
      {state.page && (
        <>
          {" · "}
          <span title={state.page.url}>{state.page.title || state.page.url}</span>
        </>
      )}
      <span className="text-muted"> · {agoText(state.lastActivityAt, now)}</span>
    </span>
  );
}

/** A run's state as a badge: green while active, red when its machine has gone quiet. */
function StateBadge({ state, stale }: { state: RunState; stale: boolean }) {
  return (
    <Badge tone={stale ? "bad" : state.state === "active" ? "ok" : "neutral"} dot>
      {state.state}
    </Badge>
  );
}

const modeNote = "mt-1 mb-2 text-xs text-muted";

/** Whether the page follows live state or polls, and why. */
export function LiveModeNote({ mode, reason }: { mode: LiveMode; reason?: string | undefined }) {
  if (mode === "live") return <p className={modeNote}>Live: updates as they happen.</p>;
  if (mode === "connecting") return <p className={modeNote}>Connecting to live updates…</p>;
  return (
    <p className={modeNote}>
      Checking every {POLL_MS / 1000} s{reason ? ` (live updates unavailable: ${reason})` : ""}.
    </p>
  );
}

/** A run's live state as facts, for the run page. */
export function LiveFacts({ state, now }: { state: RunState | undefined; now: number }) {
  if (!state)
    return <p className="text-muted">No live state: no daemon has reported on this run.</p>;
  const stale = isStale(state, now);
  return (
    <dl className={facts}>
      <dt>State</dt>
      <dd>
        <StateBadge state={state} stale={stale} />
        {stale && (
          <span className="text-bad">
            {" "}
            no activity for over {STALE_MS / 60_000} min; its machine may be gone
          </span>
        )}
      </dd>
      <dt>Holder</dt>
      <dd>
        {state.holder ? (
          <>
            {holderText(state)} · machine <code>{state.holder.machineId}</code>
            {state.holder.segmentId && (
              <>
                {" "}
                · segment <Id id={state.holder.segmentId} />
              </>
            )}
          </>
        ) : (
          "nobody"
        )}
      </dd>
      <dt>Page</dt>
      <dd>
        {state.page ? (
          <>
            {state.page.title && <>{state.page.title} · </>}
            <code>{state.page.url}</code>
          </>
        ) : (
          "—"
        )}
      </dd>
      <dt>Last tool</dt>
      <dd>
        {state.lastTool ? (
          <>
            <code>{state.lastTool.name}</code>
            {state.lastTool.ok ? "" : <span className="text-bad"> (error)</span>}
          </>
        ) : (
          "—"
        )}
      </dd>
      <dt>Last activity</dt>
      <dd>
        <LocalTime iso={state.lastActivityAt} /> ({agoText(state.lastActivityAt, now)})
      </dd>
    </dl>
  );
}
